/**
 * HOT2000 centre-of-glass U tables used by user-defined overall window validation.
 */

// Build a glazing-column row. Omitted keys are invalid combinations (no table cell).
const g = ({ s, d, t, tg2, acr, hm66, hm88, hm2 } = {}) => {
    const row = {};
    if (s != null) row["1"] = s;
    if (d != null) row["2"] = d;
    if (t != null) row["3"] = t;
    if (tg2 != null) row["4"] = tg2;
    if (acr != null) row["5"] = acr;
    if (hm66 != null) row["6"] = hm66;
    if (hm88 != null) row["7"] = hm88;
    if (hm2 != null) row["8"] = hm2;
    return row;
};

const copyRow = (row) => ({ ...row });

// Tint does not change U. Tint+Low-E uses the matching Low-E U row.
const withUAliases = (base) => ({
    ...base,
    5: copyRow(base["0"]),
    6: copyRow(base["1"]),
    7: copyRow(base["2"]),
    8: copyRow(base["3"]),
    9: copyRow(base["4"]),
    B: copyRow(base.A),
});

// Centre-of-glass U (W/m²K), vertical.
// Fill digit (code char 3 / List 132) → UCG table key:
//   0 = 13 mm air, 1 = 9 mm air, 2 = 6 mm air,
//   3 = 13 mm argon, 4 = 9 mm argon, 5 = 6 mm argon, 6 = 9 mm krypton
const UCG_VERTICAL = {
    0: withUAliases({
        // Table 16 — 13 mm air
        0: g({ s: 6.36, d: 2.81, t: 1.8, acr: 2.68, hm66: 1.24, hm88: 1.31, hm2: 0.84 }),
        1: g({ d: 1.72, t: 1.2, tg2: 0.91, hm66: 0.91, hm88: 0.96, hm2: 0.67 }),
        2: g({ d: 1.84, t: 1.28, tg2: 1.0, hm66: 0.96, hm88: 1.01, hm2: 0.7 }),
        3: g({ d: 2.01, t: 1.38, tg2: 1.13, hm66: 1.02, hm88: 1.08, hm2: 0.73 }),
        4: g({ d: 2.23, t: 1.5, tg2: 1.3, hm66: 1.09, hm88: 1.15, hm2: 0.77 }),
        A: g({ s: 5.15, d: 2.4, t: 1.61, hm66: 1.23, hm88: 1.3, hm2: 0.83 }),
    }),
    1: withUAliases({
        // Table 17 — 9 mm air
        0: g({ d: 2.98, t: 1.97, acr: 2.83, hm66: 1.44, hm88: 1.52, hm2: 1.03 }),
        1: g({ d: 1.94, t: 1.43, tg2: 1.14, hm66: 1.15, hm88: 1.19, hm2: 0.87 }),
        2: g({ d: 2.05, t: 1.5, tg2: 1.23, hm66: 1.19, hm88: 1.24, hm2: 0.89 }),
        3: g({ d: 2.22, t: 1.59, tg2: 1.35, hm66: 1.24, hm88: 1.3, hm2: 0.92 }),
        4: g({ d: 2.42, t: 1.7, tg2: 1.51, hm66: 1.31, hm88: 1.37, hm2: 0.95 }),
        A: g({ d: 2.58, t: 1.79, hm66: 1.43, hm88: 1.5, hm2: 1.02 }),
    }),
    2: withUAliases({
        // Table 18 — 6 mm air
        0: g({ d: 3.28, t: 2.24, acr: 3.09, hm66: 1.81, hm88: 1.87, hm2: 1.32 }),
        1: g({ d: 2.45, t: 1.81, tg2: 1.53, hm66: 1.54, hm88: 1.58, hm2: 1.17 }),
        2: g({ d: 2.54, t: 1.86, tg2: 1.6, hm66: 1.57, hm88: 1.62, hm2: 1.19 }),
        3: g({ d: 2.67, t: 1.93, tg2: 1.71, hm66: 1.62, hm88: 1.67, hm2: 1.21 }),
        4: g({ d: 2.83, t: 2.01, tg2: 1.84, hm66: 1.68, hm88: 1.73, hm2: 1.25 }),
        A: g({ d: 2.95, t: 2.08, hm66: 1.8, hm88: 1.85, hm2: 1.31 }),
    }),
    3: withUAliases({
        // Table 19 — 13 mm argon
        0: g({ d: 2.65, t: 1.66, acr: 2.54, hm66: 1.04, hm88: 1.13, hm2: 0.69 }),
        1: g({ d: 1.43, t: 1.0, tg2: 0.7, hm66: 0.7, hm88: 0.75, hm2: 0.52 }),
        2: g({ d: 1.57, t: 1.08, tg2: 0.8, hm66: 0.75, hm88: 0.81, hm2: 0.55 }),
        3: g({ d: 1.76, t: 1.2, tg2: 0.95, hm66: 0.82, hm88: 0.89, hm2: 0.59 }),
        4: g({ d: 2.01, t: 1.34, tg2: 1.13, hm66: 0.89, hm88: 0.97, hm2: 0.62 }),
        A: g({ d: 2.2, t: 1.46, hm66: 1.03, hm88: 1.11, hm2: 0.68 }),
    }),
    4: withUAliases({
        // Table 20 — 9 mm argon
        0: g({ d: 2.76, t: 1.78, acr: 2.63, hm66: 1.16, hm88: 1.26, hm2: 0.82 }),
        1: g({ d: 1.53, t: 1.15, tg2: 0.87, hm66: 0.87, hm88: 0.92, hm2: 0.66 }),
        2: g({ d: 1.67, t: 1.24, tg2: 0.96, hm66: 0.91, hm88: 0.97, hm2: 0.69 }),
        3: g({ d: 1.87, t: 1.35, tg2: 1.1, hm66: 0.97, hm88: 1.04, hm2: 0.72 }),
        4: g({ d: 2.11, t: 1.48, tg2: 1.28, hm66: 1.04, hm88: 1.11, hm2: 0.75 }),
        A: g({ d: 2.31, t: 1.58, hm66: 1.16, hm88: 1.24, hm2: 0.81 }),
    }),
    5: withUAliases({
        // Table 21 — 6 mm argon
        0: g({ d: 3.0, t: 1.99, acr: 2.85, hm66: 1.47, hm88: 1.54, hm2: 1.05 }),
        1: g({ d: 1.97, t: 1.46, tg2: 1.17, hm66: 1.18, hm88: 1.22, hm2: 0.89 }),
        2: g({ d: 2.08, t: 1.53, tg2: 1.26, hm66: 1.22, hm88: 1.27, hm2: 0.91 }),
        3: g({ d: 2.25, t: 1.62, tg2: 1.38, hm66: 1.27, hm88: 1.33, hm2: 0.94 }),
        4: g({ d: 2.45, t: 1.72, tg2: 1.53, hm66: 1.33, hm88: 1.39, hm2: 0.98 }),
        A: g({ d: 2.61, t: 1.81, hm66: 1.46, hm88: 1.52, hm2: 1.04 }),
    }),
    6: withUAliases({
        // Table 22 — 9 mm krypton
        0: g({ d: 2.61, t: 1.62, acr: 2.5, hm66: 0.98, hm88: 1.08, hm2: 0.64 }),
        1: g({ d: 1.35, t: 0.94, tg2: 0.64, hm66: 0.64, hm88: 0.69, hm2: 0.47 }),
        2: g({ d: 1.49, t: 1.03, tg2: 0.74, hm66: 0.7, hm88: 0.75, hm2: 0.5 }),
        3: g({ d: 1.69, t: 1.15, tg2: 0.89, hm66: 0.77, hm88: 0.83, hm2: 0.54 }),
        4: g({ d: 1.95, t: 1.29, tg2: 1.08, hm66: 0.84, hm88: 0.91, hm2: 0.57 }),
        A: g({ d: 2.15, t: 1.42, hm66: 0.98, hm88: 1.06, hm2: 0.63 }),
    }),
};

// Edge band width for centre-of-glass area calculations
const EDGE_WIDTH_MM = 63.5;

export { UCG_VERTICAL, EDGE_WIDTH_MM };
